import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { solutionCategories } from "../lib/data";
import { Icon } from "./Icon";
import SectionHeading from "./SectionHeading";

const OurSolutions = () => {
  const { t, lang, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const cat = solutionCategories[active];
  const d = cat[lang];

  return (
    <section className="py-24 md:py-32 bg-[#11203A] border-y border-white/5" data-testid="our-solutions-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionHeading label={t.ourSolutions.label} title={t.ourSolutions.heading} sub={t.ourSolutions.sub} />

        <div className="mt-14 grid lg:grid-cols-12 gap-8">
          {/* Tabs */}
          <div className="lg:col-span-4 flex lg:flex-col gap-3 overflow-x-auto pb-2 lg:pb-0">
            {solutionCategories.map((c, i) => (
              <button
                key={c.id}
                onClick={() => setActive(i)}
                data-testid={`solution-tab-${c.id}`}
                className={`flex items-center gap-4 px-5 py-4 border text-start transition-all duration-300 shrink-0 lg:w-full ${
                  active === i
                    ? "bg-[#0A1223] border-[#D4AF37] text-white"
                    : "bg-transparent border-white/12 text-slate-300 hover:border-[#D4AF37]/50"
                }`}
              >
                <span className={`w-10 h-10 grid place-items-center shrink-0 transition-colors ${active === i ? "bg-[#D4AF37] text-[#0A1223]" : "border border-[#D4AF37]/30 text-[#D4AF37]"}`}>
                  <Icon name={c.icon} size={20} />
                </span>
                <span className="font-heading font-semibold text-sm md:text-base whitespace-nowrap lg:whitespace-normal">
                  {c[lang].title}
                </span>
              </button>
            ))}
          </div>

          {/* Panel */}
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
                className="relative bg-[#0A1223] border border-white/12 overflow-hidden h-full"
                data-testid="solution-panel"
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={cat.image} alt={d.title} className="w-full h-full object-cover opacity-60" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1223] to-transparent" />
                  <h3 className="absolute bottom-5 start-7 font-heading text-2xl font-bold text-white">{d.title}</h3>
                </div>
                <div className="p-7">
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3.5">
                    {d.items.map((item) => (
                      <div key={item} className="flex items-center gap-3 text-sm text-slate-200">
                        <Check size={16} className="text-[#D4AF37] shrink-0" />
                        {item}
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => navigate("/request-quotation")}
                    data-testid="solution-rfq-btn"
                    className="btn-gold mt-8 inline-flex items-center gap-2 px-6 py-3 group"
                  >
                    {t.common.requestQuotation}
                    <ArrowRight size={18} className={`transition-transform group-hover:translate-x-1 ${isRTL ? "rotate-180 group-hover:-translate-x-1" : ""}`} />
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurSolutions;
